import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, ShieldCheck } from 'lucide-react';
import {
  APP_LOCK_AFTER_LABELS, APP_LOCK_AFTER_OPTIONS, APP_LOCK_TEXT, readAppLockAfter, type AppLockAfter,
  FRIENDLY, MEMBER_MFA_TEXT, MFA_ISSUER, MFA_POLICIES, MFA_POLICY_LABELS, MFA_TEXT, canRequireForSelf, cleanTotp, enrolRequiredText, factorLabel, groupKey,
  isTotp, isTransientAuthError, memberMfaNote, mfaErrorMessage, newFactorName, normaliseRecoveryCode, otpauthUri, type MfaPolicy,
} from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useMe } from '@/lib/hooks';
import { useUi } from '@/lib/store';
import { supabase } from '@/lib/supabase';
import { Button, Card, ErrorBox, Field, Input, Select, Spinner, Toggle, ask } from '@/components/ui';
import { QrImage } from '@/features/workforce/clock/QrImage';
import { AuthFrame, securityKey, useSecurityStatus } from './password';
import { padUntil } from './signInError';

/**
 * t413u: two-factor sign-in (TOTP via Supabase Auth). The code step after the password (in the Sign in card), set-up
 * and removal in Settings > Security, the "your organisation requires it" set-up screen, and the workspace policy.
 * The iOS app has the same screens (apps/mobile/app/login.tsx, src/settings/account.tsx), with the same wording
 * (packages/shared/src/mfa.ts).
 */

function CodeInput({ value, onChange, testId = 'mfa-code', autoFocus = true }: { value: string; onChange: (v: string) => void; testId?: string; autoFocus?: boolean }) {
  return (
    <Input value={value} onChange={e => onChange(cleanTotp(e.target.value))} inputMode="numeric" pattern="[0-9]*" autoComplete="one-time-code"
      maxLength={7} autoFocus={autoFocus} placeholder="123456" className="text-center font-mono text-lg tracking-[0.4em]" data-testid={testId} aria-label="6-digit code" />
  );
}

/** The second step of Sign in. Lives inside the Sign in card (same neutral frame). */
export function MfaChallenge({ onDone }: { onDone: () => void }) {
  const auth = useAuth();
  const [mode, setMode] = useState<'code' | 'recovery'>('code');
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const tried = useRef('');

  const submitCode = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!isTotp(code) || busy) return;
    tried.current = code;
    setBusy(true); setError(null);
    const started = Date.now();
    try {
      if (await auth.verifyMfa(code)) { onDone(); return; }
      await padUntil(started, 600);
      setError(MFA_TEXT.badCode); setCode('');
    } catch (err) { setError(mfaErrorMessage(err, FRIENDLY.offline)); } finally { setBusy(false); }
  };
  // Autofill (iOS/macOS one-time codes, password managers) fills all six digits at once: go straight on.
  useEffect(() => { if (isTotp(code) && code !== tried.current) void submitCode(); }, [code]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitRecovery = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    const started = Date.now();
    try { await auth.recoverMfa(normaliseRecoveryCode(recovery)); onDone(); } catch (err) {
      await padUntil(started, 600);
      setError(isTransientAuthError(err) ? mfaErrorMessage(err, FRIENDLY.offline) : (err as { status?: number })?.status === 429 ? 'Too many attempts. Wait a few minutes and try again.' : MFA_TEXT.badRecovery);
    } finally { setBusy(false); }
  };

  const heading = <h1 className="mb-2 text-lg font-bold">{mode === 'code' ? MFA_TEXT.challengeTitle : MFA_TEXT.recoveryTitle}<span className="mt-1.5 block h-1 w-10 rounded-full bg-accent-line" aria-hidden /></h1>;
  const footer = (
    <div className="mt-4 flex items-center justify-between text-sm">
      <button type="button" className="font-medium text-slate-600 underline-offset-2 hover:underline dark:text-slate-300" data-testid="mfa-switch"
        onClick={() => { setMode(m => (m === 'code' ? 'recovery' : 'code')); setError(null); }}>{mode === 'code' ? 'Use a recovery code' : 'Use my authenticator app'}</button>
      <button type="button" className="text-slate-500 underline-offset-2 hover:underline" onClick={() => void auth.signOut()} data-testid="mfa-cancel">Cancel</button>
    </div>
  );
  if (mode === 'recovery') {
    return (
      <div data-testid="mfa-recovery">
        {heading}
        <p className="mb-4 text-sm text-slate-600 dark:text-slate-300">{MFA_TEXT.recoveryHint}</p>
        <form onSubmit={e => void submitRecovery(e)} className="space-y-4">
          <Field label="Recovery code"><Input value={recovery} onChange={e => setRecovery(e.target.value)} autoComplete="off" autoCapitalize="none" spellCheck={false} className="font-mono" placeholder="xxxxx-xxxxx" data-testid="mfa-recovery-code" /></Field>
          <ErrorBox error={error} />
          <Button type="submit" className="w-full" loading={busy} disabled={normaliseRecoveryCode(recovery).length < 10}>Continue</Button>
        </form>
        {footer}
      </div>
    );
  }
  return (
    <div data-testid="mfa-challenge">
      {heading}
      <p className="mb-4 text-sm text-slate-600 dark:text-slate-300">{MFA_TEXT.challengeHint}</p>
      <form onSubmit={e => void submitCode(e)} className="space-y-4">
        <Field label="Code"><CodeInput value={code} onChange={setCode} /></Field>
        <ErrorBox error={error} />
        <Button type="submit" className="w-full" loading={busy} disabled={!isTotp(code)} data-testid="mfa-verify">Verify</Button>
      </form>
      {footer}
    </div>
  );
}

function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const toast = useUi(s => s.toast);
  const text = codes.join('\n');
  return (
    <div className="space-y-3" data-testid="mfa-recovery-codes">
      <p className="text-sm text-slate-600 dark:text-slate-300">{MFA_TEXT.savedCodes}</p>
      <ol className="grid grid-cols-2 gap-x-6 gap-y-1 rounded-lg bg-slate-50 p-3 font-mono text-sm dark:bg-slate-800">{codes.map(c => <li key={c}>{c}</li>)}</ol>
      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" onClick={() => void navigator.clipboard?.writeText(text).then(() => toast('Copied', 'success'))}><Copy className="h-4 w-4" />Copy</Button>
        <Button variant="secondary" onClick={() => {
          const a = document.createElement('a');
          a.href = URL.createObjectURL(new Blob([`${MFA_ISSUER} recovery codes\n\n${text}\n`], { type: 'text/plain' }));
          a.download = 'the-hub-recovery-codes.txt'; a.click(); URL.revokeObjectURL(a.href);
        }}>Download</Button>
        <Button onClick={onDone} data-testid="mfa-codes-done">I've saved them</Button>
      </div>
    </div>
  );
}

/** Scan, enter the first code, then save the recovery codes. Used in Settings and by the required set-up screen. */
export function MfaEnrol({ onDone, onCancel }: { onDone: () => void; onCancel?: () => void }) {
  const me = useMe();
  const qc = useQueryClient();
  const [factor, setFactor] = useState<{ id: string; secret: string } | null>(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const email = me.data?.profile?.email ?? '';

  useEffect(() => {
    if (!supabase) return;
    let live = true;
    void (async () => {
      try {
        // Supabase keeps abandoned (unverified) set-ups; clear them so the new one can be added.
        const list = await supabase!.auth.mfa.listFactors();
        for (const f of list.data?.all ?? []) if (f.status === 'unverified') await supabase!.auth.mfa.unenroll({ factorId: f.id });
        const { data, error: err } = await supabase!.auth.mfa.enroll({ factorType: 'totp', friendlyName: newFactorName(), issuer: MFA_ISSUER });
        if (err || !data) throw err ?? new Error('enrol failed');
        if (live) setFactor({ id: data.id, secret: data.totp.secret });
      } catch (err) { if (live) setError(isTransientAuthError(err) ? FRIENDLY.offline : 'Two-factor set-up isn’t available right now. Try again later.'); }
    })();
    return () => { live = false; };
  }, []);

  const verify = async (e: FormEvent) => {
    e.preventDefault();
    if (!factor || !supabase) return;
    setBusy(true); setError(null);
    try {
      const { error: err } = await supabase.auth.mfa.challengeAndVerify({ factorId: factor.id, code });
      if (err) { setError(mfaErrorMessage(err, FRIENDLY.offline)); setCode(''); return; }
      await api.mfaChanged('enrolled');
      const r = await api.mfaRecoveryCodes();
      setCodes(r.codes);
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(false); }
  };

  if (codes) return <RecoveryCodes codes={codes} onDone={() => { void qc.invalidateQueries({ queryKey: securityKey }); onDone(); }} />;
  return (
    <form onSubmit={e => void verify(e)} className="space-y-3" data-testid="mfa-enrol">
      <p className="text-sm text-slate-600 dark:text-slate-300">{MFA_TEXT.enrolHint}</p>
      {factor ? (
        <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center">
          <QrImage value={otpauthUri(factor.secret, email)} size={168} label="QR code for your authenticator app" />
          <div className="min-w-0 text-sm">
            <div className="text-slate-500">{MFA_TEXT.manualKey}</div>
            <code className="mt-1 block break-all font-mono text-[13px] select-all" data-testid="mfa-secret">{groupKey(factor.secret)}</code>
          </div>
        </div>
      ) : !error && <div className="flex h-40 items-center"><Spinner /></div>}
      <Field label="Code from the app"><CodeInput value={code} onChange={setCode} autoFocus={false} testId="mfa-enrol-code" /></Field>
      <ErrorBox error={error} />
      <div className="flex gap-2">
        <Button type="submit" loading={busy} disabled={!factor || !isTotp(code)} data-testid="mfa-enrol-verify">Turn on</Button>
        {onCancel && <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>}
      </div>
    </form>
  );
}

/** Settings > Security > Two-factor sign-in. */
export function TwoFactorSection() {
  const status = useSecurityStatus();
  const qc = useQueryClient();
  const toast = useUi(s => s.toast);
  const [step, setStep] = useState<'idle' | 'enrol' | 'remove' | 'codes'>('idle');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const factors = useQuery({
    queryKey: ['me', 'mfa-factors'], enabled: Boolean(supabase),
    queryFn: async () => (await supabase!.auth.mfa.listFactors()).data?.totp.filter(f => f.status === 'verified') ?? [],
  });
  const mfa = status.data?.mfa;
  const refresh = () => { void qc.invalidateQueries({ queryKey: securityKey }); void factors.refetch(); };
  if (!supabase) return null;

  // Turning it off asks for a fresh code first (a borrowed unlocked laptop isn't enough).
  const remove = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const list = factors.data ?? [];
      let ok = false;
      for (const f of list) { const { error: err } = await supabase!.auth.mfa.challengeAndVerify({ factorId: f.id, code }); if (!err) { ok = true; break; } if (isTransientAuthError(err)) throw err; }
      if (!ok) { setError(MFA_TEXT.badCode); setCode(''); return; }
      for (const f of list) await supabase!.auth.mfa.unenroll({ factorId: f.id });
      await supabase!.auth.refreshSession();
      await api.mfaChanged('removed');
      setStep('idle'); setCode(''); refresh();
      toast('Two-factor sign-in turned off', 'success');
    } catch (err) { setError(mfaErrorMessage(err, FRIENDLY.offline)); } finally { setBusy(false); }
  };
  const newCodes = async () => {
    if (!await ask({ title: 'New recovery codes?', body: 'Your old recovery codes stop working.', confirmLabel: 'Make new codes', danger: false })) return;
    try { const r = await api.mfaRecoveryCodes(); setCodes(r.codes); setStep('codes'); refresh(); } catch (err) { toast(errorMessage(err), 'error'); }
  };
  const lastRequired = Boolean(mfa?.required) && (factors.data?.length ?? 0) <= 1;

  return (
    <div className="mt-6 max-w-md space-y-2" data-testid="two-factor">
      <h3 className="flex items-center gap-2 text-sm font-semibold"><ShieldCheck className="h-4 w-4 text-slate-500" />Two-factor sign-in</h3>
      {step === 'enrol' ? <MfaEnrol onDone={() => { setStep('idle'); refresh(); toast('Two-factor sign-in is on', 'success'); }} onCancel={() => setStep('idle')} />
        : step === 'codes' && codes ? <RecoveryCodes codes={codes} onDone={() => { setStep('idle'); setCodes(null); }} />
        : !mfa?.enabled ? (
          <>
            <p className="text-xs text-slate-500">{mfa?.required ? 'Required by your organisation. ' : ''}Ask for a code from an authenticator app as well as your password when you sign in.</p>
            <Button variant="secondary" onClick={() => setStep('enrol')} data-testid="mfa-setup">Set up</Button>
          </>
        ) : (
          <>
            <ul className="divide-y divide-slate-100 text-sm dark:divide-slate-800" data-testid="mfa-factors">
              {(factors.data ?? []).map(f => <li key={f.id} className="py-1.5">{factorLabel(f)}</li>)}
            </ul>
            <p className="text-xs text-slate-500" data-testid="mfa-codes-left">{mfa.recovery_codes_left} recovery code{mfa.recovery_codes_left === 1 ? '' : 's'} left</p>
            {step === 'remove' ? (
              <form onSubmit={e => void remove(e)} className="space-y-3" data-testid="mfa-remove">
                <p className="text-sm text-slate-600 dark:text-slate-300">{MFA_TEXT.removeHint}</p>
                <Field label="Code"><CodeInput value={code} onChange={setCode} testId="mfa-remove-code" /></Field>
                <ErrorBox error={error} />
                <div className="flex gap-2">
                  <Button type="submit" variant="danger" loading={busy} disabled={!isTotp(code)} data-testid="mfa-remove-confirm">Turn off</Button>
                  <Button type="button" variant="ghost" onClick={() => { setStep('idle'); setError(null); }}>Cancel</Button>
                </div>
              </form>
            ) : (
              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" onClick={() => void newCodes()} data-testid="mfa-new-codes">New recovery codes</Button>
                {lastRequired ? <span className="self-center text-xs text-slate-500">Required by your organisation</span>
                  : <Button variant="ghost" onClick={() => setStep('remove')} data-testid="mfa-turn-off">Turn off</Button>}
              </div>
            )}
          </>
        )}
    </div>
  );
}

/** Shown instead of the app when a workspace requires two-factor and this person hasn't set it up. */
export function ForcedMfaEnrol() {
  const { signOut } = useAuth();
  const qc = useQueryClient();
  const status = useSecurityStatus();
  return (
    <AuthFrame title={MFA_TEXT.enrolTitle}>
      <p className="mb-4 text-sm text-slate-600 dark:text-slate-300" data-testid="mfa-enrol-required">{enrolRequiredText(status.data?.mfa?.required_by)}</p>
      <MfaEnrol onDone={() => { void qc.invalidateQueries(); }} />
      <button type="button" className="mt-4 w-full text-center text-sm text-slate-500 hover:underline" onClick={() => void signOut()}>Sign out</button>
    </AuthFrame>
  );
}

/** Settings > Security (workspace): who must use two-factor. */
export function SecurityPolicyCard({ ws }: { ws: string }) {
  const qc = useQueryClient();
  const toast = useUi(s => s.toast);
  const q = useQuery({ queryKey: ['ws', ws, 'security-policy'], queryFn: () => api.securityPolicy(ws) });
  const [busy, setBusy] = useState<MfaPolicy | null>(null);
  const set = async (p: MfaPolicy) => {
    if (p === q.data?.mfa_required) return;
    if (p !== 'off' && !await ask({ title: 'Require two-factor?', body: 'People it applies to are asked to set it up the next time they open the Hub.', confirmLabel: 'Require it', danger: false })) return;
    setBusy(p);
    try { const r = await api.setSecurityPolicy(ws, p); qc.setQueryData(['ws', ws, 'security-policy'], r); void qc.invalidateQueries({ queryKey: securityKey }); toast('Saved', 'success'); }
    catch (err) { toast(errorMessage(err), 'error'); } finally { setBusy(null); }
  };
  return (
    <Card title="Two-factor sign-in">
      {!q.data ? <Spinner /> : (
        <div className="max-w-md space-y-2" data-testid="mfa-policy">
          {MFA_POLICIES.map(p => (
            <label key={p} className="flex cursor-pointer items-center gap-2 text-sm">
              <input type="radio" name="mfa-policy" checked={q.data.mfa_required === p} disabled={busy !== null} onChange={() => void set(p)} data-testid={`mfa-policy-${p}`} />
              {MFA_POLICY_LABELS[p]}
            </label>
          ))}
          <p className="pt-1 text-xs text-slate-500">{q.data.with_mfa} of {q.data.members} people use two-factor.</p>
        </div>
      )}
    </Card>
  );
}

/**
 * t555u: Settings › Security (workspace): how long the iOS app may be away before its Face ID lock asks again. The
 * lock stays each person's choice in the app (Me › Lock with Face ID); this only sets the wait (an hour by default).
 */
export function AppLockPolicyCard({ ws }: { ws: string }) {
  const qc = useQueryClient();
  const toast = useUi(s => s.toast);
  const q = useQuery({ queryKey: ['ws', ws, 'security-policy'], queryFn: () => api.securityPolicy(ws) });
  const saved = q.data ? readAppLockAfter(q.data.app_lock_after_min) : null;
  const [draft, setDraft] = useState<AppLockAfter | null>(null);
  const [busy, setBusy] = useState(false);
  const value = draft ?? saved;
  const dirty = draft != null && draft !== saved;
  const save = async () => {
    if (draft == null) return;
    setBusy(true);
    try { const r = await api.setAppLockAfter(ws, draft); qc.setQueryData(['ws', ws, 'security-policy'], r); setDraft(null); void qc.invalidateQueries({ queryKey: ['me'] }); toast('Saved', 'success'); }
    catch (err) { toast(errorMessage(err), 'error'); } finally { setBusy(false); }
  };
  return (
    <Card title={APP_LOCK_TEXT.title}>
      {q.isError ? <ErrorBox error={q.error} onRetry={() => void q.refetch()} /> : value == null ? <Spinner /> : (
        <div className="max-w-md space-y-3" data-testid="app-lock-policy">
          <Field label={APP_LOCK_TEXT.label} note={APP_LOCK_TEXT.note}>
            <Select value={String(value)} disabled={busy} onChange={e => setDraft(Number(e.target.value) as AppLockAfter)} data-testid="app-lock-after">
              {APP_LOCK_AFTER_OPTIONS.map(m => <option key={m} value={m}>{APP_LOCK_AFTER_LABELS[m]}{m === 60 ? ' (default)' : ''}</option>)}
            </Select>
          </Field>
          {dirty && (
            <div className="flex justify-end gap-2">
              <Button variant="ghost" disabled={busy} onClick={() => setDraft(null)} data-testid="app-lock-cancel">Cancel</Button>
              <Button loading={busy} onClick={() => void save()} data-testid="app-lock-save">Save</Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

/** Admin: member security modal row (lost phone and recovery codes). */
export function MemberMfaRow({ ws, userId, factors, onChanged }: { ws: string; userId: string; factors: number; onChanged: () => void }) {
  const toast = useUi(s => s.toast);
  const [busy, setBusy] = useState(false);
  const reset = async () => {
    if (!await ask({ title: 'Turn off two-factor?', body: 'Use this if they lost their phone and recovery codes. They can sign in with their password and set it up again.', confirmLabel: 'Turn off', danger: true })) return;
    setBusy(true);
    try { await api.resetMemberMfa(ws, userId); toast('Two-factor turned off', 'success'); onChanged(); } catch (err) { toast(errorMessage(err), 'error'); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center justify-between gap-3 text-sm" data-testid="member-mfa">
      <span>Two-factor sign-in <span className="text-slate-500">{factors > 0 ? 'on' : 'off'}</span></span>
      {factors > 0 && <Button size="sm" variant="secondary" loading={busy} onClick={() => void reset()} data-testid="member-mfa-reset">Turn off</Button>}
    </div>
  );
}

/**
 * t462u: one person's two-factor for a workspace Owner (desktop Users page and the phone person sheet): the
 * "Require two-factor sign-in" switch, whether they've set it up, and reset (lost phone). The API refuses all of it
 * to anyone but an Owner.
 */
export function useMemberTwoFactor(ws: string | null | undefined, userId: string, enabled: boolean) {
  const qc = useQueryClient();
  const toast = useUi(s => s.toast);
  const key = ['ws', ws, 'member-two-factor', userId];
  const q = useQuery({ queryKey: key, queryFn: () => api.memberTwoFactor(ws!, userId), enabled: Boolean(ws && enabled), retry: false });
  const [busy, setBusy] = useState<'switch' | 'reset' | null>(null);
  const setRequired = async (required: boolean) => {
    setBusy('switch');
    try { qc.setQueryData(key, await api.setMemberTwoFactor(ws!, userId, required)); toast('Saved', 'success'); void qc.invalidateQueries({ queryKey: ['audit'] }); }
    catch (err) { toast(errorMessage(err), 'error'); } finally { setBusy(null); }
  };
  const reset = async () => {
    if (!await ask({ title: MEMBER_MFA_TEXT.resetTitle, body: MEMBER_MFA_TEXT.resetBody, confirmLabel: 'Reset', danger: true })) return;
    setBusy('reset');
    try { await api.resetMemberMfa(ws!, userId); toast('Two-factor sign-in reset', 'success'); await q.refetch(); }
    catch (err) { toast(errorMessage(err), 'error'); } finally { setBusy(null); }
  };
  return { q, busy, setRequired, reset };
}

/** Desktop Users › person: shown only to Owners. */
export function MemberTwoFactorCard({ ws, userId, self }: { ws: string; userId: string; self: boolean }) {
  const { q, busy, setRequired, reset } = useMemberTwoFactor(ws, userId, true);
  const m = q.data;
  if (q.isError) return null;
  return (
    <Card title={MEMBER_MFA_TEXT.title}>
      {!m ? <Spinner /> : (
        <div className="max-w-md space-y-2" data-testid="member-two-factor">
          <div className="flex items-center justify-between gap-3">
            <span className={busy === 'switch' || (self && !m.required && !canRequireForSelf(m)) ? 'pointer-events-none opacity-60' : ''} data-testid="member-two-factor-switch-wrap">
              <Toggle checked={m.required} onChange={v => void setRequired(v)} label={MEMBER_MFA_TEXT.switchLabel} />
            </span>
          </div>
          <p className="text-xs text-slate-500" data-testid="member-two-factor-status">{memberMfaNote(m, self)}</p>
          {m.set_up && !self && <Button size="sm" variant="secondary" loading={busy === 'reset'} onClick={() => void reset()} data-testid="member-two-factor-reset">{MEMBER_MFA_TEXT.reset}</Button>}
        </div>
      )}
    </Card>
  );
}
